import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CaseActivityCard from "@_components/CaseActivityCard/CaseActivityCard";
import styles from "./CaseActivitySection.module.css";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import { invoiceTemplateActions } from "@_actions/invoiceTemplate.actions";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import CallbackLogItem from "@_components/CallbackLogItem/CallbackLogItem";
import { caseActions } from "@_actions/case.actions";
import { caseConstants } from "@_constants/case.constants";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import { catchError } from "@_utils/helpers";
import AddressPopup from "@_components/popups/AddressPopup/AddressPopup";
import InfoPopup from "@_components/popups/InfoPopup/InfoPopup";
import { customerActions } from "@_actions/customer.actions";
import CaseActivityFilter from "@_components/CaseActivityFilter/CaseActivityFilter";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import AddNotePopup from "@_components/popups/AddNotePopup/AddNotePopup";

const DEFAULT_ENABLED_CATEGORIES = {
  [caseConstants.CASE_ACTIVITY_CATEGORY_COMMUNICATION]: true,
  [caseConstants.CASE_ACTIVITY_CATEGORY_USER_ACTION]: true,
  [caseConstants.CASE_ACTIVITY_CATEGORY_WEBSITE_OWNER_ACTION]: true,
  [caseConstants.CASE_ACTIVITY_CATEGORY_OTHER]: true,
};

const loadEnabledCategories = () => {
  const stored = localStorage.getItem(
    caseConstants.ACTIVITY_FILTER_STORAGE_KEY,
  );
  return stored ? JSON.parse(stored) : DEFAULT_ENABLED_CATEGORIES;
};

const saveEnabledCategories = (categories) => {
  localStorage.setItem(
    caseConstants.ACTIVITY_FILTER_STORAGE_KEY,
    JSON.stringify(categories),
  );
};

function CaseActivitySection(props) {
  const { data } = props;
  const caseId = data?.id;
  const caseActivityReducer = useSelector((state) => {
    return state.caseActivityReducer;
  });
  const caseReducer = useSelector((state) => state.caseReducer);
  const templateReducer = useSelector((state) => state.templateReducer);

  const dispatch = useDispatch();

  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [openAddressPopup, setOpenAddressPopup] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [showFutureSection, setShowFutureSection] = useState(false);
  // Derived from `data` so the label stays in sync after pause/resume
  // without needing a manual reload.
  const isAutomationPaused =
    !!data &&
    (data.num_scheduled_emails_paused > 0 ||
      data.num_scheduled_emails_failed > 0 ||
      data.num_triggered_emails_failed > 0);
  const [resumeAutomationPopup, setResumeAutomationPopup] = useState(false);
  // Tracks whether the most recent toggleAutomation dispatch was a resume, so
  // the failure handler can show the "resend failed emails" warning only for
  // resume attempts and surface the real backend error otherwise.
  const [lastToggleWasResume, setLastToggleWasResume] = useState(false);
  const [sentFailedEmailType, setSentFailedEmailType] = useState("");
  const [openInfoPopup, setOpenInfoPopup] = useState(false);
  const [infoPopupText, setInfoPopupText] = useState("");
  const [infoPopupTitle, setInfoPopupTitle] = useState("");
  const [showActivitiesError, setShowActivitiesError] = useState(false);
  const { snackbarProps, showSnackbarSuccess, showSnackbarError } =
    useSnackbar();

  const [scheduledActivities, setScheduledActivities] = useState([]);

  const [enabledCategories, setEnabledCategories] = useState(
    loadEnabledCategories,
  );

  const toggleCategory = (category) => {
    const nextEnabledCategories = {
      ...enabledCategories,
      [category]: !enabledCategories[category],
    };
    setEnabledCategories(nextEnabledCategories);
    saveEnabledCategories(nextEnabledCategories);
  };
  const activitiesStatus = caseActivityReducer?.status?.CASEACTIVITY_GET;
  const emailTemplates = useMemo(() => {
    const mailFlowId = data?.mail_flow_id;
    if (!mailFlowId) return [];
    return templateReducer.flow_templates?.[mailFlowId] || [];
  }, [data?.mail_flow_id, templateReducer.flow_templates]);

  const activities = useMemo(
    () => caseActivityReducer.activities || [],
    [caseActivityReducer.activities],
  );
  const isPriorityActivity = (x) =>
    x.priority === "HIGH" || x.priority === "LOW";

  // Some BE activity types are intentionally omitted from caseConstants and filtered out here (see case.constants.js).
  const filteredActivities = useMemo(
    () =>
      activities.filter(
        (x) =>
          (!x.content ||
            x.content.scheduled_at === undefined ||
            x.content.status === "FAILED" ||
            (x.type === "LOGIN" && !x.user_id)) &&
          !(
            x.content?.letter_status === "action_required" &&
            !x.content?.provider_id
          ) &&
          !(x.type === "LOGIN" && x.user_id) &&
          Object.values(caseConstants).includes(x.type),
      ),
    [activities],
  );

  const priorityActivities = useMemo(
    () => filteredActivities.filter(isPriorityActivity),
    [filteredActivities],
  );

  const nonPriorityActivities = useMemo(
    () => filteredActivities.filter((x) => !isPriorityActivity(x)),
    [filteredActivities],
  );

  const normalActivities = useMemo(
    () =>
      nonPriorityActivities.filter((activity) => {
        const category =
          activity.category || caseConstants.CASE_ACTIVITY_CATEGORY_OTHER;
        return !!enabledCategories[category];
      }),
    [enabledCategories, nonPriorityActivities],
  );

  const categoryCounts = useMemo(
    () =>
      nonPriorityActivities.reduce((counts, activity) => {
        const category =
          activity.category || caseConstants.CASE_ACTIVITY_CATEGORY_OTHER;
        counts[category] = (counts[category] || 0) + 1;
        return counts;
      }, {}),
    [nonPriorityActivities],
  );

  const totalNonPriority = nonPriorityActivities.length;

  /**
   * Loads activities and credits when a different case is selected.
   *
   * @dependencies
   * - data
   *
   * @triggers
   * When `data` changes.
   *
   *
   */
  useEffect(() => {
    setActivitiesLoading(true);
    setShowFutureSection(false);
    setScheduledActivities([]);
    dispatch(invoiceTemplateActions.getInvoiceTemplates());
    dispatch(customerActions.getCredits());
    if (caseId) {
      dispatch(caseActivityActions.getCaseActivities(caseId));
    } else {
      setActivitiesLoading(false);
    }
  }, [caseId, dispatch]);

  // Only prompt to resume when the resend we just completed clears the LAST
  // remaining failed email across both buckets (scheduled + triggered).
  // Previously this checked a single bucket equalling 1, which could open the
  // popup while the other bucket still had failures and the BE would reject
  // the resume.
  const handleResumeAutomationPopup = useCallback(() => {
    const remainingFailed =
      (data?.num_scheduled_emails_failed || 0) +
      (data?.num_triggered_emails_failed || 0);
    if (remainingFailed === 1 && !(scheduledActivities.length === 1)) {
      setResumeAutomationPopup(true);
    }
  }, [
    data?.num_scheduled_emails_failed,
    data?.num_triggered_emails_failed,
    scheduledActivities.length,
  ]);

  /**
   * Decrements failure counter (scheduled or triggered) when a previously failed email succeeds on retry.
   * Clear retry flag
   * Show resume automation popup
   */
  useEffect(() => {
    const responseStatusSendMail = caseReducer.status.CASE_SEND_EMAIL;
    const responseStatusSaveMail = caseReducer.status.CASE_SAVE_EMAIL_TO_LOG;
    if (
      sentFailedEmailType &&
      (responseStatusSendMail === "success" ||
        responseStatusSaveMail === "success")
    ) {
      dispatch(
        caseActions.decreaseFailed(
          caseId,
          sentFailedEmailType === caseConstants.CASEACTIVITY_TYPE_TRIGGERED
            ? "num_triggered_emails_failed"
            : "num_scheduled_emails_failed",
        ),
      );
      setSentFailedEmailType("");
      handleResumeAutomationPopup();
    }
  }, [
    dispatch,
    caseId,
    caseReducer.status.CASE_SEND_EMAIL,
    caseReducer.status.CASE_SAVE_EMAIL_TO_LOG,
    handleResumeAutomationPopup,
    sentFailedEmailType,
  ]);

  useEffect(() => {
    if (
      caseReducer.status.CASE_SEND_EMAIL === "success" &&
      !caseReducer.trigger_sending_email_failed &&
      data?.num_scheduled_emails_paused > 0 &&
      data?.num_scheduled_emails_failed === 0 &&
      data?.num_triggered_emails_failed === 0
    ) {
      setResumeAutomationPopup(true);
    }
  }, [
    caseReducer.status.CASE_SEND_EMAIL,
    caseReducer.trigger_sending_email_failed,
    data?.num_scheduled_emails_paused,
    data?.num_scheduled_emails_failed,
    data?.num_triggered_emails_failed,
  ]);

  /**
   * Shows success/error feedback after generating invoice.
   *
   * @triggers
   * When invoice generation status changes.
   *
   * @user_flow
   * 1. Generate invoice completes.
   * 2. Success: snackbar, close address dialog, `getCaseActivities`.
   * 3. Error: error snackbar, clear CASE_GENERATE_INVOICE state.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_GENERATE_INVOICE === "success") {
      showSnackbarSuccess("Success!");
      setOpenAddressPopup(false);
      dispatch(caseActivityActions.getCaseActivities(data.id));
    } else if (catchError(caseReducer.status.CASE_GENERATE_INVOICE)) {
      showSnackbarError(caseReducer.status.CASE_GENERATE_INVOICE);
      dispatch(caseActivityActions.clearState("CASE_GENERATE_INVOICE"));
    }
  }, [
    dispatch,
    data.id,
    caseReducer.status.CASE_GENERATE_INVOICE,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  /**
   * Reacts to an email-automation toggle result.
   *
   * On success, refetches the case activities so each scheduled email's
   * content.status / paused_at reflect the new automation state (keeping the
   * timestamp label and tooltip in sync).
   *
   * On failure, the backend rejects a resume request while the case still has
   * failed scheduled emails. In that case we surface a clear warning telling
   * the user that all failed emails must be resent before the automation can
   * resume, then clear the status so the message is only shown once.
   */
  useEffect(() => {
    const toggleAutomationStatus = caseReducer.status.CASE_TOGGLE_AUTOMATION;
    if (toggleAutomationStatus === "success") {
      // Refetch activities so each scheduled email's content.status /
      // paused_at reflect the new automation state; otherwise the timestamp
      // label and tooltip keep showing the pre-toggle value.
      if (caseId) {
        dispatch(caseActivityActions.getCaseActivities(caseId));
      }
      dispatch(caseActions.clearStateMessage("CASE_TOGGLE_AUTOMATION"));
    } else if (catchError(toggleAutomationStatus)) {
      const isFailedEmailsError =
        lastToggleWasResume || /failed.*emails?/i.test(toggleAutomationStatus);
      showSnackbarError(
        isFailedEmailsError
          ? "The email automation cannot be resumed while failed emails remain. Please resend all failed emails before resuming the automation."
          : toggleAutomationStatus,
      );
      dispatch(caseActions.clearStateMessage("CASE_TOGGLE_AUTOMATION"));
    }
  }, [
    caseReducer.status.CASE_TOGGLE_AUTOMATION,
    caseId,
    dispatch,
    showSnackbarError,
    lastToggleWasResume,
  ]);

  /**
   * One-shot UI when `trigger_sending_email_failed` is set on `caseReducer` (email send failed outside normal status).
   *
   * Used to show a generic error and immediately `restoreTriggerEmailFailed` so the flag does not stick.
   *
   * @triggers
   * When trigger_sending_email_failed flag is set
   *
   * @user_flow
   * 1. Some send path sets `trigger_sending_email_failed`.
   * 2. Show error snackbar; dispatch restore to clear flag.
   */
  useEffect(() => {
    if (caseReducer.trigger_sending_email_failed) {
      showSnackbarError("Something went wrong. Email was not sent");
      dispatch(caseActions.restoreTriggerEmailFailed());
    }
  }, [caseReducer.trigger_sending_email_failed, dispatch, showSnackbarError]);

  useEffect(() => {
    if (catchError(activitiesStatus)) {
      setShowActivitiesError(true);
      showSnackbarError(activitiesStatus || "Failed to load activities.");
      dispatch(caseActivityActions.clearState("CASEACTIVITY_GET"));
    } else if (
      activitiesStatus === "request" ||
      activitiesStatus === "success"
    ) {
      setShowActivitiesError(false);
    }
  }, [activitiesStatus, dispatch, showSnackbarError]);

  const activateAutomation = () => {
    // Skip the round-trip: the BE rejects a resume while failed emails remain,
    // so warn immediately when the FE already knows there are failures.
    if (
      (data?.num_scheduled_emails_failed || 0) > 0 ||
      (data?.num_triggered_emails_failed || 0) > 0
    ) {
      showSnackbarError(
        "The email automation cannot be resumed while failed emails remain. Please resend all failed emails before resuming the automation.",
      );
      setResumeAutomationPopup(false);
      return;
    }
    setLastToggleWasResume(true);
    dispatch(
      caseActions.toggleAutomation(
        data.id,
        caseConstants.CASE_AUTOMATION_STATE_SCHEDULED,
      ),
    );
    setResumeAutomationPopup(false);
  };

  /**
   * After a note is deleted (`CASEACTIVITY_DELETE` success), refetch activities and clear status.
   *
   * Used so the activity list immediately drops the removed note.
   *
   * @dependencies
   * - deleteNoteState, data.id, dispatch
   *
   * @triggers
   * When delete succeeds.
   *
   * @user_flow
   * 1. `deleteNote` completes.
   * 2. `getCaseActivities`; `clearState("CASEACTIVITY_DELETE")`.
   *
   *    * @problem
   * No handling of delete-note failure case, which could leave users without feedback
   */
  useEffect(() => {
    const deleteNoteStatus = caseActivityReducer?.status?.CASEACTIVITY_DELETE;

    if (deleteNoteStatus === "success") {
      if (data?.id) {
        dispatch(caseActivityActions.getCaseActivities(data.id));
      }
      dispatch(caseActivityActions.clearState("CASEACTIVITY_DELETE"));
    } else if (catchError(deleteNoteStatus)) {
      showSnackbarError(deleteNoteStatus);
      dispatch(caseActivityActions.clearState("CASEACTIVITY_DELETE"));
    }
  }, [
    caseActivityReducer?.status?.CASEACTIVITY_DELETE,
    data?.id,
    dispatch,
    showSnackbarError,
  ]);

  /**
   * Updates loading state and filters scheduled activities when activities load.
   *
   * @dependencies
   * - caseActivityReducer, activities, CASEACTIVITY_GET status
   *
   * @triggers
   * When reducer activities or GET status change.
   *
   * @user_flow
   * 1. Activities fetch completes → loading false.
   * 2. Filter scheduled future emails (not SENT) into `scheduledActivities`.
   * issue – Loader can stay forever on failed fetch, activitiesLoading is set to false only for "success".
   * If CASEACTIVITY_GET fails, users may see an endless spinner instead of an error/empty state
   */
  useEffect(() => {
    const state = caseActivityReducer.status.CASEACTIVITY_GET;

    if (state === "request") {
      setActivitiesLoading(true);
      return;
    }

    setActivitiesLoading(false);

    if (state === "success") {
      if (activities.length > 0) {
        setScheduledActivities(
          activities.filter(
            (x) =>
              x.content &&
              x.content.scheduled_at !== null &&
              x.content.scheduled_at !== undefined /**todo */ &&
              x.content.status !== caseConstants.CASE_ACTIVITY_STATE_SENT,
          ),
        );
      } else {
        setScheduledActivities([]);
      }
    } else {
      setScheduledActivities([]);
    }
  }, [activities, caseActivityReducer.status.CASEACTIVITY_GET]);

  /**
   * Shows feedback and refreshes case activities after invoice cancel succeeds.
   */
  useEffect(() => {
    if (caseActivityReducer.status.INVOICE_CANCEL === "success") {
      showSnackbarSuccess("Success!");
      dispatch(caseActivityActions.getCaseActivities(data.id));
    } else if (catchError(caseActivityReducer.status.INVOICE_CANCEL)) {
      showSnackbarError(caseActivityReducer.status.INVOICE_CANCEL);
    }
    dispatch(caseActivityActions.clearState("INVOICE_CANCEL"));
    // eslint-disable-next-line
  }, [
    caseActivityReducer.status.INVOICE_CANCEL,
    dispatch,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  // Priority activities (priority HIGH/LOW) bypass the category filter.
  const passesCategoryFilter = (activity) => {
    if (isPriorityActivity(activity)) return true;
    const category =
      activity.category || caseConstants.CASE_ACTIVITY_CATEGORY_OTHER;
    return !!enabledCategories[category];
  };

  const renderActivityCard = (x, index) => (
    <div
      key={x.id || "case_activity_card_number_" + index.toString()}
      className={styles.activity_container}
    >
      <CaseActivityCard
        activity={x}
        caseData={data}
        emailTemplates={emailTemplates}
        // Only pass a resume callback when automation is actually paused.
        // MailPopup treats a truthy resumeAutomation as "this is a resume
        // send" and hides the reminder, so passing it unconditionally would
        // suppress the reminder on every reply.
        // Safe to inline (no useCallback): resumeAutomation is not used in any
        // downstream useEffect/useCallback/useMemo dependency array, so a new
        // function identity per render never re-triggers child effects.
        resumeAutomation={
          isAutomationPaused
            ? () => setSentFailedEmailType(x.content?.type || "")
            : null
        }
      />
    </div>
  );

  const priorityItems = priorityActivities.map(renderActivityCard);

  const normalItems = normalActivities.map(renderActivityCard);

  const onPauseToggleClicked = () => {
    setLastToggleWasResume(isAutomationPaused);
    dispatch(
      caseActions.toggleAutomation(
        data.id,
        isAutomationPaused
          ? caseConstants.CASE_AUTOMATION_STATE_SCHEDULED
          : caseConstants.CASE_AUTOMATION_STATE_PAUSED,
      ),
    );
  };

  const visibleScheduledActivities = scheduledActivities
    .filter((a) => a.content.status !== "FAILED")
    .filter(passesCategoryFilter);

  const hasScheduled = useMemo(
    () => visibleScheduledActivities.length > 0,
    [visibleScheduledActivities],
  );

  const totalNonPriorityScheduled = useMemo(
    () =>
      visibleScheduledActivities.filter(
        (a) => a.priority !== "HIGH" && a.priority !== "LOW",
      ).length,
    [visibleScheduledActivities],
  );

  const showFilterRow = totalNonPriority + totalNonPriorityScheduled > 0;
  const showOtherActivitiesDivider =
    normalItems.length > 0 && (priorityItems.length > 0 || hasScheduled);
  const noFilteredItemsVisible =
    showFilterRow && normalItems.length === 0 && !hasScheduled;

  const sortedVisibleScheduledActivities = useMemo(
    () =>
      visibleScheduledActivities.slice().sort((x1, x2) => {
        const date1 = new Date(x1.content.scheduled_at);
        const date2 = new Date(x2.content.scheduled_at);
        return date2 - date1;
      }),
    [visibleScheduledActivities],
  );

  const renderFutureList = () => {
    let items = sortedVisibleScheduledActivities.map((x, index) => {
      return (
        <div key={x.id || index} className={styles.activity_container}>
          <CaseActivityCard
            activity={x}
            caseData={data}
            resumeAutomation={() => setSentFailedEmailType(x.content.type)}
            dottedborder
          ></CaseActivityCard>
        </div>
      );
    });
    if (items && items.length >= 2 && showFutureSection === false) {
      const hiddenCount = sortedVisibleScheduledActivities.length - 1;
      items = items.slice(items.length - 1);
      items.unshift(
        <div
          key={"__unique_show_future_id__"}
          className={styles.activity_container_more_button}
        >
          <Button
            data-testid="case-activity-show-more-scheduled-btn"
            variant="outlined"
            onClick={() => setShowFutureSection(true)}
            className={styles.add_note_button}
          >
            Show {hiddenCount} more Scheduled Email
            {hiddenCount === 1 ? "" : "s"}
          </Button>
        </div>,
      );
    }
    return items;
  };

  const openInvoiceBlockedPopup = (title, message) => {
    setInfoPopupText(message);
    setInfoPopupTitle(title);
    setOpenInfoPopup(true);
  };

  const handleOpenAddressPopup = () => {
    if (
      caseActivityReducer.activities.some(
        (activity) =>
          activity.type === caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED &&
          activity.content?.type === "ACTIVE",
      )
    ) {
      openInvoiceBlockedPopup(
        "Cannot generate an Invoice",
        "An active Invoice already exists. Please cancel it before generating a new invoice.",
      );
      return;
    } else if (!caseReducer.case[data.id]?.invoice_template_id) {
      openInvoiceBlockedPopup(
        "Cannot generate an Invoice",
        "No invoice template set for this case. Please assign an invoice template before generating an invoice",
      );
      return;
    } else setOpenAddressPopup(true);
  };

  return (
    <div className={styles.root}>
      <AppSnackbar {...snackbarProps} />
      {data.callback_request && (
        <CallbackLogItem data={data.callback_request} caseId={data.id} />
      )}
      <hr
        style={{
          backgroundColor: "var(--color-theme-cloud)",
          marginBottom: "15px",
        }}
      ></hr>
      <div className={styles.title_container}>
        <div className={styles.title_font}>Case Activities</div>
        <div className={styles.title_right_side}>
          <div className={styles.flex_right}>
            {scheduledActivities.length > 0 &&
            !(
              scheduledActivities.length === 1 &&
              scheduledActivities[0].content?.status ===
                caseConstants.CASE_ACTIVITY_STATE_FAILED
            ) ? (
              <FormControlLabel
                labelPlacement="start"
                disabled={data && data.num_scheduled_emails_failed > 0}
                label={
                  isAutomationPaused
                    ? "Automation is paused"
                    : "Automation is active"
                }
                control={
                  <div>
                    <Tooltip
                      title={
                        data &&
                        (data.num_scheduled_emails_failed > 0 ||
                          data.num_triggered_emails_failed > 0)
                          ? "Resolve failed emails before resuming"
                          : ""
                      }
                      placement="top"
                      arrow
                    >
                      <span>
                        <Button
                          data-testid="case-activity-pause-toggle-btn"
                          style={{
                            marginLeft: "20px",
                            marginRight: "11px",
                            width: "101px",
                            color: "var(--color-font-10) !important",
                            borderColor: "var(--color-font-10) !important",
                            position: "relative",
                            zIndex: 99,
                          }}
                          disabled={
                            data &&
                            (data.num_scheduled_emails_failed > 0 ||
                              data.num_triggered_emails_failed > 0)
                          }
                          className={styles.add_note_button}
                          variant="outlined"
                          onClick={onPauseToggleClicked}
                        >
                          {isAutomationPaused ? "Resume" : "Pause"}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                }
              ></FormControlLabel>
            ) : (
              <></>
            )}
          </div>
          <div>
            {data.state !== "STANDBY" && data.state !== "CANCELLED" && (
              <Button
                data-testid="case-activity-generate-invoice-btn"
                variant="outlined"
                style={{ marginRight: "25px" }}
                onClick={handleOpenAddressPopup}
                className={styles.add_note_button}
              >
                GENERATE INVOICE
              </Button>
            )}
            <Button
              data-testid="case-activity-add-note-btn"
              variant="outlined"
              onClick={() => setAddNoteDialogOpen(true)}
              className={styles.add_note_button}
            >
              ADD NOTE
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.activity_list_container}>
        {activitiesLoading ? (
          <div className={styles.loading}>
            <CircularProgress style={{ color: "black" }} />
          </div>
        ) : showActivitiesError ? (
          <div
            className={styles.filter_empty_message}
            data-testid="case-activity-error-state"
          >
            {"Failed to load activities."}
          </div>
        ) : (
          <>
            {priorityItems}
            {showFilterRow && (
              <CaseActivityFilter
                enabledCategories={enabledCategories}
                onToggleCategory={toggleCategory}
                categoryCounts={categoryCounts}
              />
            )}
            {hasScheduled && (
              <div
                className={
                  priorityItems.length > 0
                    ? `${styles.future_section_visible} ${styles.after_priority_gap}`
                    : styles.future_section_visible
                }
              >
                {renderFutureList()}
              </div>
            )}
            {showOtherActivitiesDivider ? (
              <div className={styles.priority_divider_container}>
                <div className={styles.priority_divider_border} />
                <span className={styles.priority_divider_content}>Now</span>
                <div className={styles.priority_divider_border} />
              </div>
            ) : null}
            {normalItems}
            {noFilteredItemsVisible && (
              <div
                className={styles.filter_empty_message}
                data-testid="case-activity-no-filtered-message"
              >
                No activities match the selected filters.
              </div>
            )}
            {!priorityItems.length && !normalItems.length && !hasScheduled && (
              <div
                className={styles.filter_empty_message}
                data-testid="case-activity-no-activities-message"
              >
                No activities found.
              </div>
            )}
          </>
        )}
      </div>
      <AddressPopup
        open={openAddressPopup}
        onClose={() => setOpenAddressPopup(false)}
        case={data}
      />
      <AddNotePopup
        open={addNoteDialogOpen}
        close={() => setAddNoteDialogOpen(false)}
        caseId={data?.id}
      />
      <AreYouSurePopup
        open={resumeAutomationPopup}
        close={() => setResumeAutomationPopup(false)}
        onClick={activateAutomation}
      >
        The email automation is currently paused. Do you want to resume it?
      </AreYouSurePopup>
      <InfoPopup
        data-testid="case-activity-info-popup"
        open={openInfoPopup}
        close={() => setOpenInfoPopup(false)}
        title={infoPopupTitle}
      >
        <p>{infoPopupText}</p>
      </InfoPopup>
    </div>
  );
}

export default CaseActivitySection;
